import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Boxes,
  ChevronDown,
  Circle,
  Eye,
  Highlighter,
  Image as ImageIcon,
  ListChecks,
  Mic,
  PenLine,
  Plus,
  Shapes,
  Sparkles,
  Square,
  Tag,
  Trash2,
  Type,
  Underline as UnderlineIcon,
  Video,
  Volume2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type {
  AnnotationKind,
  ElementType,
  RevealStep,
  ScriptElement,
  Slide,
} from '@/lib/schema.types';
import { cn } from '@/lib/utils';

interface Props {
  slide: Slide;
  steps: RevealStep[];
  selectedStepId: string | null;
  /** When in playback, this is the step currently playing (highlighted). */
  playingStepId?: string | null;
  onSelect: (stepId: string) => void;
  onAddRevealElement: (elementId: string) => void;
  onAddEmpty: () => void;
  onDelete: (stepId: string) => void;
  onReorder: (ids: string[]) => void;
}

export function StepTimeline({
  slide,
  steps,
  selectedStepId,
  playingStepId,
  onSelect,
  onAddRevealElement,
  onAddEmpty,
  onDelete,
  onReorder,
}: Props) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
        <h3 className="text-[10px] font-semibold uppercase tracking-[0.16em] text-fg-muted">
          Step Timeline
        </h3>
        <span className="text-[11px] text-fg-subtle">
          {steps.length} step{steps.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 scroll-thin">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            const oldIndex = steps.findIndex((s) => s.id === active.id);
            const newIndex = steps.findIndex((s) => s.id === over.id);
            if (oldIndex < 0 || newIndex < 0) return;
            onReorder(arrayMove(steps, oldIndex, newIndex).map((s) => s.id));
          }}
        >
          <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {steps.map((step, i) => (
                <StepCard
                  key={step.id}
                  step={step}
                  index={i}
                  selected={step.id === selectedStepId}
                  isPlaying={step.id === playingStepId}
                  slide={slide}
                  onSelect={() => onSelect(step.id)}
                  onDelete={() => onDelete(step.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {steps.length === 0 && (
          <div className="rounded-md border border-dashed border-border bg-bg-subtle/40 p-4 text-center text-xs text-fg-muted">
            No steps yet. Use <span className="font-semibold">+ New step</span> below to add a
            reveal, an annotation, or a narration.
          </div>
        )}
      </div>

      <div className="border-t border-border-subtle p-3">
        <AddStepMenu slide={slide} onAddReveal={onAddRevealElement} onAddEmpty={onAddEmpty} />
      </div>
    </div>
  );
}

// ---------- step card ----------

function StepCard({
  step,
  index,
  selected,
  isPlaying,
  slide,
  onSelect,
  onDelete,
}: {
  step: RevealStep;
  index: number;
  selected: boolean;
  isPlaying: boolean;
  slide: Slide;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  } as React.CSSProperties;

  const summary = describeStep(step, slide);

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      {...attributes}
      {...listeners}
      className={cn(
        'group/step relative cursor-grab rounded-md border bg-white px-3 py-2.5 transition active:cursor-grabbing',
        isPlaying
          ? 'border-success bg-success/5 ring-1 ring-success/40'
          : selected
            ? 'border-accent bg-accent-soft/40 shadow-pill ring-1 ring-accent/30'
            : 'border-border hover:border-accent/40',
      )}
    >
      <div className="flex items-center justify-between">
        <span
          className={cn(
            'text-[10px] font-semibold uppercase tracking-[0.14em]',
            isPlaying ? 'text-success' : selected ? 'text-accent' : 'text-fg-muted',
          )}
        >
          Trigger {String(index + 1).padStart(2, '0')}
        </span>
        <span className="font-mono text-[10px] text-fg-subtle">
          {formatDuration(step.audio?.durationMs)}
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5">
        <span className="grid h-4 w-4 place-items-center text-fg-muted">{summary.icon}</span>
        <span className="truncate text-[12px] font-medium text-fg">{summary.label}</span>
      </div>
      {summary.subtitle && (
        <div className="mt-0.5 truncate pl-5 text-[10px] text-fg-subtle">{summary.subtitle}</div>
      )}

      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute right-1 top-1 hidden h-5 w-5 items-center justify-center rounded text-fg-subtle hover:text-red-500 group-hover/step:flex"
        title="Delete step"
      >
        <Trash2 size={11} />
      </button>
    </div>
  );
}

// ---------- add step popover ----------

function AddStepMenu({
  slide,
  onAddReveal,
  onAddEmpty,
}: {
  slide: Slide;
  onAddReveal: (elementId: string) => void;
  onAddEmpty: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const elements = flattenElements(slide);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border-strong bg-bg-subtle/40 px-3 py-2.5 text-[12px] font-semibold uppercase tracking-wider text-fg-muted transition hover:border-accent hover:text-accent"
      >
        <Plus size={14} /> New step
        <ChevronDown size={12} className={cn('ml-1 transition', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 right-0 z-30 mb-2 max-h-[55vh] overflow-y-auto rounded-md border border-border bg-white p-2 shadow-floating scroll-thin">
          <div className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-fg-muted">
            Reveal an element
          </div>
          {elements.length === 0 && (
            <div className="px-2 py-2 text-[11px] text-fg-subtle">
              No elements on this slide yet — add some in the editor.
            </div>
          )}
          <div className="space-y-1">
            {elements.map((item) => (
              <button
                key={item.element.id}
                onClick={() => {
                  onAddReveal(item.element.id);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left transition hover:bg-bg-subtle"
              >
                <span
                  className={cn(
                    'grid h-5 w-5 shrink-0 place-items-center rounded text-fg-muted',
                    iconBg[item.element.type],
                  )}
                >
                  {iconForType(item.element.type)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] font-medium text-fg">
                    {labelForElement(item.element)}
                  </span>
                  <span className="block text-[10px] text-fg-subtle">
                    Row {item.rowIndex + 1} · Col {item.columnIndex + 1}
                  </span>
                </span>
                <Eye size={12} className="text-fg-subtle" />
              </button>
            ))}
          </div>

          <div className="mt-2 border-t border-border-subtle pt-2">
            <button
              onClick={() => {
                onAddEmpty();
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left transition hover:bg-bg-subtle"
            >
              <span className="grid h-5 w-5 place-items-center rounded bg-bg-subtle text-fg-muted">
                <Mic size={12} />
              </span>
              <span className="text-[12px] font-medium text-fg">Empty step (for narration)</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- helpers ----------

export interface FlatElement {
  element: ScriptElement;
  rowIndex: number;
  columnIndex: number;
}

export function flattenElements(slide: Slide): FlatElement[] {
  const out: FlatElement[] = [];
  slide.rows.forEach((row, rowIndex) => {
    row.columns.forEach((col, columnIndex) => {
      col.elements.forEach((element) => out.push({ element, rowIndex, columnIndex }));
    });
  });
  return out;
}

function describeStep(
  step: RevealStep,
  slide: Slide,
): { icon: React.ReactNode; label: string; subtitle?: string } {
  const elementMap = new Map<string, ScriptElement>();
  slide.rows.forEach((r) =>
    r.columns.forEach((c) => c.elements.forEach((e) => elementMap.set(e.id, e))),
  );
  const firstElementName = (ids: string[] = []) => {
    for (const id of ids) {
      const el = elementMap.get(id);
      if (el) return labelForElement(el);
    }
    return undefined;
  };

  if (step.audio?.text || step.audio?.src) {
    const subtitle = (step.elementIds?.length ?? 0) > 0
      ? `Reveals: ${(step.elementIds ?? [])
          .map((id) => labelForElement(elementMap.get(id)))
          .filter(Boolean)
          .join(', ')}`
      : undefined;
    return {
      icon: <Volume2 size={11} />,
      label: `Audio: ${truncate(step.label || step.audio.text || 'Narration', 30)}`,
      subtitle,
    };
  }
  const ann = step.annotations?.[0];
  if (ann) {
    const target = elementMap.get(ann.targetElementId);
    return {
      icon: iconForAnnotation(ann.kind),
      label: `${capitalize(ann.kind)}: ${truncate(target ? labelForElement(target) : 'element', 28)}`,
    };
  }
  if ((step.elementIds?.length ?? 0) > 0) {
    const name = firstElementName(step.elementIds);
    const more = (step.elementIds?.length ?? 1) - 1;
    return {
      icon: <Eye size={11} />,
      label: `Reveal: ${truncate(name ?? 'element', 28)}${more > 0 ? ` +${more}` : ''}`,
    };
  }
  return { icon: <Mic size={11} />, label: step.label || 'Empty step' };
}

export function labelForElement(el?: ScriptElement): string {
  if (!el) return 'element';
  switch (el.type) {
    case 'text': {
      const raw = typeof el.content === 'string' ? el.content : '';
      const stripped = raw.replace(/<[^>]*>/g, '').trim();
      return stripped || 'Text';
    }
    case 'image':
      return (el.content as { alt?: string } | undefined)?.alt || 'Image';
    case 'shape':
      return `${(el.content as { shape?: string } | undefined)?.shape ?? 'Shape'} shape`;
    case 'mcq':
      return (el.content as { question?: string } | undefined)?.question || 'MCQ';
    case 'chip':
      return (el.content as { label?: string } | undefined)?.label || 'Chip';
    case 'video':
      return 'Video';
    case 'embed':
      return 'Embed';
    case '3d':
      return '3D model';
    default:
      return 'Element';
  }
}

const iconBg: Record<ElementType, string> = {
  text: 'bg-blue-50 text-blue-600',
  image: 'bg-amber-50 text-amber-600',
  shape: 'bg-violet-50 text-violet-600',
  mcq: 'bg-emerald-50 text-emerald-600',
  chip: 'bg-pink-50 text-pink-600',
  video: 'bg-rose-50 text-rose-600',
  embed: 'bg-cyan-50 text-cyan-600',
  '3d': 'bg-indigo-50 text-indigo-600',
  latex: 'bg-orange-50 text-orange-600',
  icon: 'bg-indigo-50 text-indigo-600',
  callout: 'bg-red-50 text-red-600',
  mermaid: 'bg-teal-50 text-teal-600',
  timeline: 'bg-lime-50 text-lime-600',
};

function iconForType(type: ElementType) {
  switch (type) {
    case 'text':
      return <Type size={12} />;
    case 'image':
      return <ImageIcon size={12} />;
    case 'shape':
      return <Shapes size={12} />;
    case 'mcq':
      return <ListChecks size={12} />;
    case 'chip':
      return <Tag size={12} />;
    case 'video':
      return <Video size={12} />;
    case 'embed':
      return <Sparkles size={12} />;
    case '3d':
      return <Boxes size={12} />;
    default:
      return <Square size={12} />;
  }
}

function iconForAnnotation(kind: AnnotationKind): React.ReactNode {
  switch (kind) {
    case 'highlight':
      return <Highlighter size={11} />;
    case 'encircle':
      return <Circle size={11} />;
    case 'underline':
      return <UnderlineIcon size={11} />;
    case 'annotate':
      return <PenLine size={11} />;
  }
}

function formatDuration(ms?: number) {
  if (!ms) return '—';
  const total = Math.round(ms / 1000);
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return `0:${mm}:${ss}`;
}

function truncate(s: string, n: number) {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
